import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AuthShell, { inputCls, labelCls } from "./AuthShell";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import { money } from "@/lib/constants";
import { useSeo } from "@/lib/seo";

const NAMES = { diagnostic: "Infant Sleep Diagnostic ($99)", blueprint: "Personalized Blueprint ($279)",
  guided: "Guided Virtual Consulting ($749)", inhome_overnight: "In-Home + Overnight ($2,549)",
  is101_tuition: "IS-101 Foundations of Infant Sleep ($99 tuition)", ns101_tuition: "NS-101 Naps, Wake Windows & Daily Rhythm ($149 tuition)", is201_tuition: "IS-201 Night Wakings & Independent Settling ($299 tuition)", is301_tuition: "IS-301 Sleep Regressions & Developmental Changes ($299 tuition)", is401_tuition: "IS-401 Bedtime, Early Waking & the Crib Transition ($299 tuition)" };

export default function Register() {
  useSeo({ title: "Create Account | Infant Sleep Consultant Near Me Education Center", noindex: true });
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const product = NAMES[sp.get("product")] ? sp.get("product") : null;
  const rawNext = sp.get("next") || "";
  const next = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/portal";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const u = await register(email, password, name);
      if (product) {
        localStorage.removeItem("isc_pending_product");
        return startCheckout({ product, user: u, navigate: nav });
      }
      nav(next);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title={product ? "Create your account" : "Create Your Student Account"}
      subtitle={product ? `Create an account to continue to ${NAMES[product]}. Your progress is always saved.` : "Join the Infant Sleep Consultant Near Me Education Center. Create your complimentary student account to begin your studies, access educational resources, and explore our academic courses."}
      footer={<>{product ? "Already have an account?" : "Already a student?"} <Link to={product ? `/login?product=${product}` : `/login${rawNext ? `?next=${encodeURIComponent(next)}` : ""}`} data-testid="register-signin-link" className="text-[#C2836B] hover:underline">Sign In</Link></>}>
      {product && <div className="mb-5 text-sm text-[#C2836B] bg-[#C2836B]/10 border border-[#C2836B]/25 rounded-lg px-4 py-3">Next: secure checkout for {NAMES[product]}</div>}
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Full Name</label>
          <input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} required />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} required />
        </div>
        <div>
          <label className={labelCls}>Password</label>
          <input data-testid="register-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} minLength={6} required />
        </div>
        {err && <p data-testid="register-error" className="text-[#e08a8a] text-sm">{err}</p>}
        <button data-testid="register-submit" disabled={busy} className="btn-clay w-full py-3 rounded-full text-sm disabled:opacity-60">
          {busy ? "Creating…" : product ? "Create account & continue" : "Create My Student Account"}
        </button>
      </form>
      <p className="text-xs text-[#6b665e] mt-5 leading-relaxed">Accounts belong to the parent or legal guardian. Your child's information is stored only as a profile you manage.</p>
    </AuthShell>
  );
}
